import {Injectable} from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivate,
    CanActivateChild,
    PRIMARY_OUTLET,
    Router,
    RouterStateSnapshot,
    UrlTree
} from '@angular/router';
import {ApiService} from '../api/api.service';
import {OnboardingError} from '../api/errors';
import {HttpErrorResponse} from '@angular/common/http';
import {environment} from '../../environments/environment';


@Injectable()
export class OnboardingGuard implements CanActivate, CanActivateChild {

    constructor(private readonly api: ApiService,
                private readonly router: Router) {
    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        // generic/totp-not-existing errors can be reached freely
        if (this.isInErrorRoute(state.url, OnboardingError.GENERIC, OnboardingError.TOTP_NOT_EXISTING)) {
            return true;
        }

        const totp = route.queryParamMap.get('totp');
        if (!totp) {
            console.error('No TOTP specified');
            return this.mkErrorUrl(totp, OnboardingError.GENERIC);
        }

        // in case of url mismatch, force navigation to generic error page;
        // if totp is not found altogether, navigate to totp-not-existing error page
        return this.api.getCustomerConfig(totp).toPromise()
            .then(customerConfig => this.checkSameUrl(customerConfig.onboarding_app_url) ?
                true : this.mkErrorUrl(totp, OnboardingError.GENERIC))
            .catch(err => this.mkErrorUrl(totp,
                err instanceof HttpErrorResponse && err.status === 404 ? OnboardingError.TOTP_NOT_EXISTING : OnboardingError.GENERIC));
    }

    canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        return this.canActivate(route, state);
    }

    private mkErrorUrl(totp: string, error: OnboardingError): UrlTree {
        return this.router.createUrlTree(['/c', 'onboarding-invalid'], {
            queryParams: {totp, error},
            queryParamsHandling: 'merge'
        });
    }

    private checkSameUrl(onboardingUrl: string): boolean {
        // Check for local development
        if (!environment.production) {
            return true;
        }
        try {
            const url = new URL(onboardingUrl);
            return location.origin === url.origin;
        } catch (err) {
            console.error('Invalid URL:', onboardingUrl);
            return false;
        }
    }

    private isInErrorRoute(url: string, ...errors: OnboardingError[]): boolean {
        const urlTree = this.router.parseUrl(url);
        const segments = urlTree.root.children[PRIMARY_OUTLET].segments;
        return segments.length > 1 && segments[0].path === 'c' && segments[1].path === 'onboarding-invalid' &&
            errors.includes(urlTree.queryParamMap.get('error') as OnboardingError);
    }
}
